import { MAZE_SPEC, type MazeSlotSpec, type MazeSlotsSpec } from './mazeSpec.generated';

/**
 * ============================================================================
 * THE SCORING ROW - the seven pockets along the bottom of the machine
 * ============================================================================
 *
 * This is the ONLY thing in the game that scores. Nothing inside the maze scores
 * any more: the maze is a labyrinth the ball has to be guided *through*, and the
 * reward is which pocket it lands in at the bottom.
 *
 * The row is FIXED TO THE CABINET. It does not rotate with the board. That is
 * the whole point of it being separate: the board turns to aim, the row stays
 * put, and the ball's exit angle decides the pocket. If the row turned too, the
 * pockets would slide out from under the ball while it was falling and the aim
 * would stop meaning anything.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY NUMBER IS DERIVED
 * ---------------------------------------------------------------------------
 * Every value below is computed from `slots` in `blender/spec/maze.json`, which
 * is also what `blender/scripts/lib/board.py` reads to DRAW the row. The
 * drawing and the scoring therefore cannot disagree: adding a tier to the spec
 * moves the dividers, changes the pocket values and updates the display in one
 * edit, and none of them can be one edit ahead of the others.
 *
 * The derivation mirrors `board.scoring_row_geometry()` line for line. If that
 * function changes, this one must change with it - which is exactly why both
 * are kept to a handful of divisions of the same three spec values.
 */

export interface ScoreRowGeometry {
  /** Number of pockets. */
  count: number;
  /** Half the row's nominal width, i.e. where the outermost dividers sit. */
  span: number;
  /** Pitch from one divider to the next. */
  width: number;
  /** Pocket depth, from the mouth down to the floor. */
  depth: number;
  /** World Y of the row's open top - the plane the ball crosses to score. */
  mouthY: number;
  /** Z of the back panels' centres. */
  backZ: number;
  /** Z of the shared front lip's centre. */
  frontZ: number;
  /** Thickness of a divider, the floor and the front lip. */
  divider: number;
  /** World Y of the floor's centre. */
  floorY: number;
  /** World Y of the row's mid-depth - the centre of every divider. */
  midY: number;
  /** World Z of the row's mid-depth - the centre of every divider. */
  midZ: number;
  /** Clear width of a pocket, i.e. between its two dividers. */
  innerWidth: number;
  /** Full width including the two end walls. */
  totalWidth: number;
  /** Outer half-extent of the row, end walls included. */
  outerHalfWidth: number;
  /** Full depth of the row along Z. */
  zExtent: number;
}

function derive(slots: MazeSlotsSpec): ScoreRowGeometry {
  const count = slots.count;
  const span = slots.spanRadius;
  const width = (2 * span) / count;
  const divider = slots.dividerThickness;
  return {
    count,
    span,
    width,
    depth: slots.depth,
    mouthY: slots.mouthY,
    backZ: slots.backZ,
    frontZ: slots.frontZ,
    divider,
    floorY: slots.mouthY - slots.depth,
    midY: slots.mouthY - slots.depth / 2,
    midZ: (slots.backZ + slots.frontZ) / 2,
    innerWidth: width - divider,
    totalWidth: 2 * span + divider,
    outerHalfWidth: span + divider / 2,
    zExtent: slots.frontZ - slots.backZ,
  };
}

export const SCORE_ROW: ScoreRowGeometry = derive(MAZE_SPEC.slots);

/** The pockets, left to right. The centre one is the bonus pocket. */
export function slotsLeftToRight(): readonly MazeSlotSpec[] {
  return MAZE_SPEC.slots.entries;
}

/**
 * Resolve a pocket from the ball's X as it crosses the row's mouth.
 *
 * This is the ONLY place a score is decided, and it is a pure function of one
 * coordinate. Nothing here consults a mesh name, a material or a pixel colour:
 * those are presentation and are free to change without silently changing what
 * the player scores.
 *
 * The pocket intervals are contiguous, each spanning `centerX +/- halfWidth`,
 * and they tile the row exactly because `halfWidth` is half the divider pitch.
 * The boundary between two pockets is therefore the CENTRE of the divider
 * between them, which is the only choice that is symmetric - a boundary at a
 * divider's face would make a ball resting against that face score differently
 * depending on which side of the machine it came from.
 *
 * Intervals are half-open `[left, right)` so the boundaries cannot overlap. A
 * ball cannot physically come to rest exactly on a boundary anyway: a divider
 * is a solid wall there.
 */
export function resolveSlot(x: number): MazeSlotSpec | null {
  const entries = MAZE_SPEC.slots.entries;
  if (entries.length === 0) return null;

  for (const entry of entries) {
    if (x >= entry.centerX - entry.halfWidth && x < entry.centerX + entry.halfWidth) return entry;
  }

  // Outside every interval but still inside the row's footprint: that is only
  // possible within an end wall's own thickness, where a ball cannot come to
  // rest. Snap to the end pocket rather than reporting a miss for a ball that is
  // plainly inside the machine.
  if (Math.abs(x) <= SCORE_ROW.outerHalfWidth) {
    return x < 0 ? entries[0] : entries[entries.length - 1];
  }

  return null;
}

/** Pocket lookup by id, for the display and the tests. */
export function slotById(id: string): MazeSlotSpec | null {
  return MAZE_SPEC.slots.entries.find((entry) => entry.id === id) ?? null;
}

/** The bonus pocket: the one at the exact bottom centre. */
export function bonusSlot(): MazeSlotSpec {
  const entries = MAZE_SPEC.slots.entries;
  return entries[Math.floor(entries.length / 2)];
}

/**
 * The X the ball must cross the mouth at to win the bonus.
 *
 * Zero by construction - the row is symmetric and the centre pocket is built
 * on it - but read from the spec rather than written as a literal, so a spec
 * that moved the row could not leave this behind.
 */
export const BONUS_X = bonusSlot().centerX;
